import SignUp from "app/pages/SignUp";
import { Suspense, lazy } from "react";
import { RouteObject } from "react-router-dom";
import Login from "screens/Login";
import LoadingScreen from "../common/Loading";
import Layout from "./components/Layout";
import PrivateRoute from "./components/PrivateRoute";
import { DesktopChatLayout } from "./DesktopLayout/Routes";
import { MobileChatLayout } from "./MobileLayout/Routes";
import AddAvailability from "./pages/AddAvailability/AddAvailability";
import AllAvailabilities from "./pages/AllAvailabilities";
import AllItems from "./pages/AllItems";
import BookableItemPage from "./pages/BookableItemPage";
import BookingDetails from "./pages/Bookings/BookingDetails";
import BookingsAndReferrals from "./pages/Bookings/BookingsAndReferrals";
import BusinessList from "./pages/business-list/BusinessList";
import CreateBusinessList from "./pages/business-list/CreateBusinessList";
import BusinessCheckout from "./pages/BusinessCheckout/BusinessCheckout";
import BusinessProfile from "./pages/Businesses/BusinessProfile";
import Checkout from "./pages/Checkout";
import RequestFollowUp from "./pages/Checkout/RequestFollowUp";
import EditBasicItem from "./pages/EditItem/EditBasicItem";
import EditRefereeItem from "./pages/EditItem/EditRefereeItem";
import ForgotPassword from "./pages/ForgotPassword/ForgotPassword";
import { Home } from "./pages/Home";
import FareharborIntegration from "./pages/Integrations/FareharborIntegration";
import Integrations from "./pages/Integrations/Integrations";
import PlaidPage from "./pages/Onboarding/Plaid";
import Profile from "./pages/Profile";
import CreateQRList from "./pages/qr-list/CreateQRList";
import QRListScreen from "./pages/qr-list/QRListScreen";
import Referees from "./pages/Referees";
import ReportDetails from "./pages/Report/ReportsDetails";
import ResetPassword from "./pages/ResetPassword/ResetPassword";
import VerifyOTP from "./pages/VerifyOTP/VerifyOTP";
const Loadable = (Component: any) => (props: JSX.IntrinsicAttributes) => (
  <Suspense fallback={<LoadingScreen />}>
    <Component {...props} />
  </Suspense>
);
const AuthPage = Loadable(lazy(() => import("app/pages/AuthPage")));

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const routes: RouteObject[] = [
  {
    path: "/",
    element: <AuthPage />,
  },
  {
    path: "/sign-up",
    element: <SignUp />,
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
  },
  {
    path: "/verify-otp",
    element: <VerifyOTP />,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
  },
  {
    path: "/home",
    element: (
      <PrivateRoute>
        <Layout>
          <Home />
        </Layout>
      </PrivateRoute>
    ),
  },
  {
    path: "/referees",
    element: (
      <PrivateRoute>
        <Layout>
          <Referees />
        </Layout>
      </PrivateRoute>
    ),
  },
  {
    path: "/businesslist/:id",
    element: (
      <PrivateRoute>
        <Layout>
          <BusinessList />
        </Layout>
      </PrivateRoute>
    ),
  },
  {
    path: "/businesslist/create/:id",
    element: (
      <PrivateRoute>
        <Layout>
          <CreateBusinessList />
        </Layout>
      </PrivateRoute>
    ),
  },
  {
    path: "/qrlist/:id",
    element: (
      <PrivateRoute>
        <Layout>
          <QRListScreen />
        </Layout>
      </PrivateRoute>
    ),
  },
  {
    path: "/qrlist/create/:id",
    element: (
      <PrivateRoute>
        <Layout>
          <CreateQRList />
        </Layout>
      </PrivateRoute>
    ),
  },
  {
    path: "/profile/:id",
    element: (
      <PrivateRoute>
        <Layout>
          <Profile />
        </Layout>
      </PrivateRoute>
    ),
  },
  {
    path: "/my-items",
    element: (
      <PrivateRoute>
        <Layout>
          <AllItems />
        </Layout>
      </PrivateRoute>
    ),
  },
  {
    path: "/edit/item/:id",
    element: (
      <PrivateRoute>
        <Layout>
          <EditRefereeItem />
        </Layout>
      </PrivateRoute>
    ),
  },
  {
    path: "/edit/basicItem/:id",
    element: (
      <PrivateRoute>
        <Layout>
          <EditBasicItem />
        </Layout>
      </PrivateRoute>
    ),
  },
  {
    path: "/availability/item/:id",
    element: (
      <PrivateRoute>
        <Layout>
          <AddAvailability />
        </Layout>
      </PrivateRoute>
    ),
  },
  {
    path: "/availabilities/:id",
    element: (
      <PrivateRoute>
        <Layout>
          <AllAvailabilities />
        </Layout>
      </PrivateRoute>
    ),
  },
  ...(isMobile ? MobileChatLayout : DesktopChatLayout),
  {
    path: "/book/:itemId/",
    element: (
      <PrivateRoute>
        <BookableItemPage />
      </PrivateRoute>
    ),
  },
  {
    path: "/book/:itemId/:timeId",
    element: (
      <PrivateRoute>
        <BookableItemPage />
      </PrivateRoute>
    ),
  },
  {
    path: "onboard",
    element: <PlaidPage />,
  },
  {
    path: "/onboard/:id",
    element: <PlaidPage />,
  },
  {
    path: "/checkout/:id",
    element: <Checkout />,
    children: [
      {
        index: true,
        element: <Checkout />,
      },
    ],
  },
  {
    path: "/business/checkout/:id",
    element: <BusinessCheckout />,
  },
  {
    path: "/checkout/:id/:url",
    element: <Checkout />,
    children: [
      {
        index: true,
        element: <Checkout />,
      },
    ],
  },
  {
    path: "/settings/integration",
    element: (
      <PrivateRoute>
        <Layout>
          <Integrations />
        </Layout>
      </PrivateRoute>
    ),
  },
  {
    path: "/settings/integration/fareharbor",
    element: (
      <PrivateRoute>
        <Layout>
          <FareharborIntegration />
        </Layout>
      </PrivateRoute>
    ),
  },
  {
    path: "/request-follow-up/:id",
    element: <RequestFollowUp />,
    children: [
      {
        index: true,
        element: <RequestFollowUp />,
      },
    ],
  },
  {
    path: "/business/:id",
    element: <BusinessProfile />,
  },
  {
    path: "/bookings/:id",
    element: (
      <PrivateRoute>
        <BookingsAndReferrals />
      </PrivateRoute>
    ),
  },
  {
    path: "/booking-details/:id",
    element: (
      <PrivateRoute>
        <BookingDetails />
      </PrivateRoute>
    ),
  },
  {
    path: "/reports/:id",
    element: (
      <PrivateRoute>
        <Layout>
          <ReportDetails />
        </Layout>
      </PrivateRoute>
    ),
  },
];

export default routes;
